import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, MessageCircle, FileText } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSEO, serviceJsonLd } from "@/hooks/useSEO";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/lib/analytics";
import { seoPageBySlug } from "@/data/seoPages";

export default function SeoLanding({ slug }) {
  const page = seoPageBySlug(slug);
  const contact = useContact();
  const waNum = (contact.whatsapp || "").replace(/[^0-9]/g, "");
  const waHref = waNum ? `https://wa.me/${waNum}` : "/request-quotation";

  useSEO({
    title: page?.title,
    description: page?.description,
    keywords: page?.keywords,
    image: page?.image,
    breadcrumbs: page ? [{ name: "Home", path: "/" }, { name: page.crumb, path: `/${page.slug}` }] : undefined,
    jsonLd: page
      ? [
          serviceJsonLd({ name: page.h1, description: page.description, serviceType: page.serviceType }),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: page.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : undefined,
  });

  if (!page) return <Navigate to="/" replace />;

  return (
    <Layout>
      <section className="relative bg-[#0A1128] py-16 lg:py-20 overflow-hidden" data-testid="seo-hero">
        <div className="absolute inset-0">
          <img src={page.image} alt={`${page.h1} — WAAN METAL Singapore`} className="w-full h-full object-cover opacity-25" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0A1128]/92 to-[#1E293B]/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-5">
            <Link to="/" className="hover:text-[#D97706]">Home</Link><ChevronRight size={13} /><span className="text-slate-300">{page.crumb}</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight max-w-3xl">{page.h1}</h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg leading-relaxed">{page.intro}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/request-quotation" onClick={() => trackEvent("quotation_request", { source: page.slug })} data-testid="seo-quote-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#B87333] text-white font-bold hover:bg-[#a9652c] transition-colors">Request a Quotation <ArrowRight size={18} /></Link>
            <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_click", { source: page.slug })} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"><MessageCircle size={18} /> WhatsApp Us</a>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">What we cover</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {page.covers.map((c) => (
                  <div key={c} className="flex items-start gap-2.5 text-slate-700"><Check size={18} className="text-[#B87333] shrink-0 mt-0.5" />{c}</div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900">Why choose WAAN METAL</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {page.why.map((w) => (
                  <div key={w} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 text-sm text-slate-700"><Check size={18} className="text-[#B87333] shrink-0 mt-0.5" />{w}</div>
                ))}
              </div>
            </div>

            {(page.priceAll || page.priceCats.length > 0) && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Related renovation prices</h2>
                <p className="mt-2 text-slate-600">Browse our published, itemised Singapore rates for this scope.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {page.priceAll && (
                    <Link to="/price-guide" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0A1128] text-white text-sm font-semibold"><FileText size={15} /> Full Price Guide</Link>
                  )}
                  {page.priceCats.map((c) => (
                    <Link key={c} to={`/price-guide?cat=${encodeURIComponent(c)}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-semibold hover:border-[#B87333] hover:text-[#B87333] transition-colors">{c}</Link>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="text-2xl font-bold text-slate-900">Frequently asked questions</h2>
              <Accordion type="single" collapsible className="mt-5 space-y-3">
                {page.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border border-slate-200 rounded-xl px-5" data-testid={`seo-faq-${i}`}>
                    <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="rounded-2xl bg-[#0A1128] p-7 text-white">
              <h3 className="text-xl font-bold">Get a free quotation</h3>
              <p className="mt-2 text-sm text-slate-300">Share your scope and photos — we'll itemise a transparent quote.</p>
              <Link to="/request-quotation" onClick={() => trackEvent("quotation_request", { source: `${page.slug}-aside` })} className="mt-5 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#B87333] text-white font-bold hover:bg-[#a9652c] transition-colors">Request a Quotation <ArrowRight size={16} /></Link>
              <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_click", { source: `${page.slug}-aside` })} className="mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10"><MessageCircle size={15} /> WhatsApp Us</a>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900">Explore related services</h3>
              <ul className="mt-3 space-y-2">
                {page.serviceLinks.map((l) => (
                  <li key={l.to}><Link to={l.to} className="inline-flex items-center gap-1.5 text-[#B87333] font-semibold text-sm hover:gap-2.5 transition-all">{l.label} <ArrowRight size={14} /></Link></li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
